import { API_CONFIG, TIMEOUTS } from '../constants/config';

export interface MoodBoard {
  id: number;
  name: string;
  description?: string;
  colors: string[];
  images: string[];
  tags: string[];
  location?: string;
  is_public: boolean;
  likes_count: number;
  shares_count: number;
  views_count: number;
  saves_count: number;
  created_at: string;
  updated_at: string;
  user_id: number;
  user_name?: string; // Display name of the user who created the board
  user_profile_image?: string;
  is_liked?: boolean;
  is_saved?: boolean;
  is_hidden?: boolean;
}

export interface CreateMoodBoardRequest {
  name: string;
  description?: string;
  colors: string[];
  images: string[];
  tags: string[];
  location?: string;
  is_public: boolean;
}

export interface CreateMoodBoardWithImagesRequest {
  name: string;
  description?: string;
  colors: string[];
  tags: string[];
  location?: string;
  is_public: boolean;
  images?: Array<{
    uri: string;
    type: string;
    name: string;
  }>; // React Native file objects for upload
}

export interface UpdateMoodBoardRequest {
  name?: string;
  description?: string;
  colors?: string[];
  images?: string[];
  tags?: string[];
  location?: string;
  is_public?: boolean;
}

export interface MoodBoardLikeResponse {
  id: number;
  liked: boolean;
  likes_count: number;
}

export interface MoodBoardPaginatedResponse {
  items: MoodBoard[];
  total: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

// Create axios instance with configuration
import axios from 'axios';
import {
  createTokenRefreshInterceptor,
  getValidAccessToken,
  getAxiosAuthConfig,
} from '../utils/tokenInterceptor';

const api = axios.create({
  ...API_CONFIG,
  timeout: TIMEOUTS.API_REQUEST, // Standard timeout - optimized endpoints
  ...getAxiosAuthConfig(),
});

// Add auth token to requests (skip when cookie mode - browser sends cookies)
api.interceptors.request.use(async (config) => {
  const token = await getValidAccessToken();
  if (token && token !== 'cookie') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add token refresh interceptor
createTokenRefreshInterceptor(api);

export const moodBoardAPI = {
  /**
   * Create a new mood board (legacy method - for backward compatibility)
   */
  create: async (data: CreateMoodBoardRequest): Promise<MoodBoard> => {
    // Convert to FormData format for new backend
    const formData = new FormData();
    formData.append('name', data.name.substring(0, 15)); // Enforce 15 char limit
    if (data.description)
      formData.append('description', data.description.substring(0, 100));
    formData.append('colors', JSON.stringify(data.colors));
    formData.append('tags', JSON.stringify(data.tags.slice(0, 10)));
    if (data.location) formData.append('location', data.location);
    formData.append('is_public', data.is_public.toString());

    const response = await api.post<MoodBoard>('/mood-boards/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Create a new mood board with image uploads
   */
  createWithImages: async (
    data: CreateMoodBoardWithImagesRequest
  ): Promise<MoodBoard> => {
    const formData = new FormData();
    formData.append('name', data.name.substring(0, 15)); // Enforce 15 char limit
    if (data.description)
      formData.append('description', data.description.substring(0, 100));
    formData.append('colors', JSON.stringify(data.colors));
    formData.append('tags', JSON.stringify(data.tags.slice(0, 10)));
    if (data.location) formData.append('location', data.location);
    formData.append('is_public', data.is_public.toString());

    // Add image files
    if (data.images && data.images.length > 0) {
      data.images.forEach((image) => {
        // React Native FormData accepts objects with uri, type, name
        formData.append(
          'images',
          image as string | { uri: string; type?: string; name?: string }
        );
      });
    }

    const response = await api.post<MoodBoard>('/mood-boards/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  /**
   * Get mood boards with filtering options
   */
  getMoodBoards: async (options?: {
    public_only?: boolean;
    user_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<MoodBoardPaginatedResponse> => {
    const params = new URLSearchParams();
    if (options?.public_only) params.append('public_only', 'true');
    if (options?.user_id) params.append('user_id', options.user_id.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await api.get<MoodBoardPaginatedResponse>(
      `/mood-boards/?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get a specific mood board
   */
  getMoodBoard: async (id: number): Promise<MoodBoard> => {
    const response = await api.get<MoodBoard>(`/mood-boards/${id}`);
    return response.data;
  },

  /**
   * Update a mood board
   */
  update: async (
    id: number,
    data: UpdateMoodBoardRequest
  ): Promise<MoodBoard> => {
    const response = await api.put<MoodBoard>(`/mood-boards/${id}`, data);
    return response.data;
  },

  /**
   * Delete a mood board
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/mood-boards/${id}`);
  },

  /**
   * Toggle like/unlike for a mood board
   */
  toggleLike: async (id: number): Promise<MoodBoardLikeResponse> => {
    const response = await api.post<MoodBoardLikeResponse>(
      `/mood-boards/${id}/like`
    );
    return response.data;
  },

  /**
   * Increment share count for a mood board
   */
  share: async (id: number): Promise<MoodBoard> => {
    const response = await api.post<MoodBoard>(`/mood-boards/${id}/share`);
    return response.data;
  },

  /**
   * Toggle save/unsave for a mood board
   */
  toggleSave: async (id: number): Promise<MoodBoardLikeResponse> => {
    const response = await api.post<MoodBoardLikeResponse>(
      `/mood-boards/${id}/save`
    );
    return response.data;
  },

  /**
   * Get public mood boards for community feed
   */
  getPublicMoodBoards: async (options?: {
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<MoodBoardPaginatedResponse> => {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.search) params.append('search', options.search);

    const response = await api.get<MoodBoardPaginatedResponse>(
      `/mood-boards/community/public?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get user's own mood boards
   */
  getUserMoodBoards: async (
    userId: number,
    options?: {
      limit?: number;
      offset?: number;
    }
  ): Promise<MoodBoardPaginatedResponse> => {
    const params = new URLSearchParams();
    params.append('user_id', userId.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await api.get<MoodBoardPaginatedResponse>(
      `/mood-boards/?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get mood boards that current user has liked
   */
  getLikedMoodBoards: async (options?: {
    limit?: number;
    offset?: number;
  }): Promise<MoodBoardPaginatedResponse> => {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await api.get<MoodBoardPaginatedResponse>(
      `/mood-boards/liked?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get mood boards that current user has saved
   */
  getSavedMoodBoards: async (options?: {
    limit?: number;
    offset?: number;
  }): Promise<MoodBoardPaginatedResponse> => {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await api.get<MoodBoardPaginatedResponse>(
      `/mood-boards/saved?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Report a mood board with a reason
   */
  report: async (id: number, reason: string): Promise<void> => {
    await api.post(`/mood-boards/${id}/report`, { reason });
  },
};
